import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  CreditCard,
  AlertCircle,
  Loader2
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getUserWebIDs, WebIDData } from '../../services/webids';
import { Button } from '../ui';
import WebIDCard from './WebIDCard';

/**
 * @component WebIDsList
 * @description Lista completa de WebIDs del usuario
 */

type StatusFilter = 'all' | 'active' | 'paused' | 'lost' | 'unclaimed';

const WebIDsList: React.FC = () => {
  const { user } = useAuth();
  const [webIds, setWebIds] = useState<WebIDData[]>([]);
  const [filteredWebIds, setFilteredWebIds] = useState<WebIDData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    if (user) {
      loadWebIds();
    }
  }, [user]);

  useEffect(() => {
    filterWebIds();
  }, [webIds, searchTerm, statusFilter]);

  const loadWebIds = async () => {
    try {
      setLoading(true);
      setError(null);

      const userWebIds = await getUserWebIDs(user!.uid);
      setWebIds(userWebIds);
    } catch (err) {
      console.error('Error loading WebIDs:', err);
      setError('Error al cargar las pulseras');
    } finally {
      setLoading(false);
    }
  };

  const filterWebIds = () => {
    let filtered = webIds;

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(webId => webId.status === statusFilter);
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(webId =>
        webId.id.toLowerCase().includes(term) ||
        ((webId.emergencyProfile as any).fullName || (webId.emergencyProfile as any).name || '').toLowerCase().includes(term) ||
        webId.emergencyProfile.phone.includes(term)
      );
    }

    setFilteredWebIds(filtered);
  };

  const getStatusCount = (status: StatusFilter) => {
    if (status === 'all') return webIds.length;
    return webIds.filter(webId => webId.status === status).length;
  };

  const statusOptions = [
    { value: 'all', label: 'Todas', count: getStatusCount('all') },
    { value: 'active', label: 'Activas', count: getStatusCount('active') },
    { value: 'paused', label: 'Pausadas', count: getStatusCount('paused') },
    { value: 'lost', label: 'Perdidas', count: getStatusCount('lost') },
    { value: 'unclaimed', label: 'Sin activar', count: getStatusCount('unclaimed') },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Mis Pulseras NFC
          </h1>
          <p className="text-gray-600">
            Gestiona todas tus pulseras de emergencia
          </p>
        </div>
        
        <Link
          to="/activar"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Vincular nueva pulsera
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={loadWebIds}
            className="ml-auto"
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por WebID, nombre o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredWebIds.length} pulsera{filteredWebIds.length !== 1 ? 's' : ''} encontrada{filteredWebIds.length !== 1 ? 's' : ''}
          </h2>
        </div>

        <div className="p-6">
          {filteredWebIds.length === 0 ? (
            <div className="text-center py-12">
              {webIds.length === 0 ? (
                <>
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No tienes pulseras NFC
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Vincula tu primera pulsera para comenzar a usar el sistema de emergencias.
                  </p>
                  <Link
                    to="/activar"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Vincular primera pulsera
                  </Link>
                </>
              ) : (
                <>
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No se encontraron pulseras
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Intenta cambiar los filtros de búsqueda.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWebIds.map((webId) => (
                <WebIDCard
                  key={webId.id}
                  webId={webId}
                  onUpdate={loadWebIds}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {webIds.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Resumen
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statusOptions.slice(1).map((option) => (
              <div key={option.value} className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {option.count}
                </div>
                <div className="text-sm text-gray-500">
                  {option.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebIDsList;
